import { listAdminSubmissions, type SubmissionList, type SubmissionRecord } from './api';

/** 三种模式均保留签名图；number 另外将选择题选项映射为序号。 */
export type ExportMode = 'signed' | 'text' | 'number';

/** 在写入 Excel 时确定实际单元格类型，避免把数值仅显示成数字。 */
export function exportCellValue(value: string | number, mode: ExportMode): string | number {
  if (mode === 'signed') return value;
  if (mode === 'text') return String(value);
  if (typeof value === 'number') return value;
  const text = value.trim();
  // 只转换单个十进制数；多选、单位、日期、空白及说明文字保持原文。
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return value;
  // 保留带前导零的编号，以及超过 Excel 15 位有效数字精度的内容。
  if (/^[+-]?0\d/.test(text) || text.replace(/[+\-.]/g, '').replace(/^0+/, '').length > 15) return value;
  const number = Number(text);
  return Number.isFinite(number) ? number : value;
}

export function recordsQuery(surveyId: string, keyword: string, from: string, to: string, status: 'valid' | 'invalid' | 'deleted' = 'valid') {
  if (from && to && from > to) throw new Error('开始日期不能晚于结束日期，请调整后重试。');
  return {
    surveyId,
    status,
    keyword: keyword.trim(),
    from: from ? new Date(`${from}T00:00:00`).getTime() : undefined,
    to: to ? new Date(`${to}T23:59:59.999`).getTime() : undefined,
    order: 'sequence_asc',
  };
}

/** API 每次最多返回 100 条；导出独立取齐当前筛选结果，不使用表格当前页。 */
export async function collectExportRecords(
  filters: ReturnType<typeof recordsQuery>,
  onProgress: (loaded: number, total: number) => void = () => {},
  fetchPage: typeof listAdminSubmissions = listAdminSubmissions,
): Promise<SubmissionRecord[]> {
  const records: SubmissionRecord[] = [];
  const ids = new Set<number>();
  let total: number | undefined;
  do {
    const page: SubmissionList = await fetchPage({ ...filters, limit: 100, offset: records.length });
    if (!Number.isInteger(page.total) || page.total < 0 || !Array.isArray(page.items)) {
      throw new Error('导出数据格式异常，请重新查询后重试。');
    }
    if (total !== undefined && page.total !== total) {
      throw new Error('导出期间记录数量发生变化，请重新查询后再导出。');
    }
    total = page.total;
    if ((!page.items.length && records.length < total) || records.length + page.items.length > total) {
      throw new Error('未能取得全部记录，请重新查询后再导出。');
    }
    for (const record of page.items) {
      if (record.surveyId !== filters.surveyId || ids.has(record.id)) {
        throw new Error('导出记录发生变化，请重新查询后再导出。');
      }
      ids.add(record.id);
      records.push(record);
    }
    onProgress(records.length, total);
  } while (records.length < total);
  return records;
}

export function saveDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Safari 需要在下载开始后才释放对象 URL。
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function fetchDocument(url: string): Promise<Response> {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    if (body?.error === 'no_documents' || body?.error === 'no_document') {
      throw new Error('暂无可下载的签署文档。电子签名可在详情中查看，也可随 Excel 导出。');
    }
    if (response.status === 401) throw new Error('登录已过期，请刷新页面并重新登录。');
    throw new Error(`文档加载失败（${response.status}），请重试。`);
  }
  return response;
}
